/**
 * Instanced rendering rig for traffic. Owned by `traffic`.
 *
 * One InstancedMesh per vehicle kind plus nine shared ones. Total draw calls at full
 * population: 6 bodies + wheels + cyclist rider + bike + ped body + ped head + 3 lamp types
 * + 1 light pool = 15, against a 40-call allowance.
 *
 * MATERIAL CONTRACT (ARCHITECTURE.md §4.4). Nothing here constructs a THREE.Material.
 * Everything comes from `ctx.materials`. Where per-instance colour is needed we take a
 * PRIVATE clone via `instanceOf()` (which we are allowed to mutate) and set
 * `vertexColors = true` so three's `diffuse = color × vColor × instanceColor` chain works.
 *
 * A private clone does not receive `look`'s weather updates, so `GradedMaterial` mirrors the
 * four properties `look` actually drives — roughness, colour grade, env intensity, night
 * emissive — off the SHARED material every fixed step. That is four float copies per
 * material per step and it means a traffic car goes matte-and-dark in the rain along with
 * the rest of the city instead of staying stubbornly dry.
 */

import * as THREE from 'three';
import { TUNING } from './tuning.js';
import { makePoolTexture, makeStreakTexture, hexToLinear } from './models.js';
import { TRAFFIC, CHAR, TRUCK } from '../materials/palette.js';

const T = TUNING;
const V = T.visual;

/* ═══════════════════════════════════════════════════════════ graded material ══ */

/**
 * A private clone that tracks the shared material's weather response. `base` is the palette
 * colour the shared material was authored with; dividing it out leaves the pure grade, which
 * then multiplies cleanly with whatever absolute colour we push through instanceColor.
 */
class GradedMaterial {
  constructor(materials, key, baseHex) {
    this.shared = materials.get(key);
    this.mat = materials.instanceOf(key);
    this.mat.vertexColors = true;
    this.mat.name = key + ':traffic';
    const b = hexToLinear(baseHex);
    this.br = Math.max(b[0], 1e-3);
    this.bg = Math.max(b[1], 1e-3);
    this.bb = Math.max(b[2], 1e-3);
    this.sync();
  }

  sync() {
    const s = this.shared, m = this.mat;
    m.color.setRGB(s.color.r / this.br, s.color.g / this.bg, s.color.b / this.bb);
    if ('roughness' in m) m.roughness = s.roughness;
    if ('metalness' in m) m.metalness = s.metalness;
    if ('envMapIntensity' in m) m.envMapIntensity = s.envMapIntensity;
    if ('emissiveIntensity' in m) m.emissiveIntensity = s.emissiveIntensity;
  }

  dispose() { this.mat.dispose(); }
}

/* ═════════════════════════════════════════════════════════════════════ rig ══ */

export class TrafficRig {
  constructor(ctx, models) {
    this.ctx = ctx;
    this.models = models;

    this.group = new THREE.Group();
    this.group.name = 'traffic';
    this.group.matrixAutoUpdate = false;
    ctx.scene.add(this.group);

    this._dummy = new THREE.Object3D();
    this._dummy.rotation.order = 'YXZ';
    this._col = new THREE.Color();

    this._buildMaterials();
    this._buildMeshes();

    // Per-kind instance counters. Allocated once — `begin()` runs every fixed step and must
    // not allocate (N9).
    this._nBody = new Int32Array(this.kindList.length);
    this._n = { wheel: 0, head: 0, tail: 0, ind: 0, pool: 0, cyclist: 0, ped: 0, blob: 0, streak: 0 };
    this._streakCol = [0, 0, 0];

    this.drawn = { bodies: 0, wheels: 0, lamps: 0, pools: 0, cyclists: 0, peds: 0, blobs: 0, streaks: 0 };
  }

  // ---------------------------------------------------------------- materials

  _buildMaterials() {
    const mats = this.ctx.materials;

    this.paint = new GradedMaterial(mats, 'traffic.paint', TRAFFIC.paints[0]);
    this.cloth = new GradedMaterial(mats, 'char.cloth', CHAR.cloth[0]);
    this.skin = new GradedMaterial(mats, 'char.skin', CHAR.skin[0]);
    this.tyre = new GradedMaterial(mats, 'traffic.tyre', TRUCK.tyre);
    this._graded = [this.paint, this.cloth, this.skin, this.tyre];

    // Lamp faces use the SHARED unlit materials directly — no per-instance colour needed,
    // so no clone, so they keep whatever `look` decides emissive gain should be.
    this.headMat = mats.get('traffic.light');
    this.brakeMat = mats.get('traffic.light.brake');
    this.indMat = mats.get('truck.light.beacon');

    // The fake headlight cone: a private clone with a generated gradient and additive blend.
    this.poolTex = makePoolTexture(V.poolTex);
    this.poolMat = mats.instanceOf('traffic.light');
    this.poolMat.name = 'traffic.lightpool';
    this.poolMat.map = this.poolTex;
    this.poolMat.transparent = true;
    this.poolMat.depthWrite = false;
    this.poolMat.depthTest = true;
    this.poolMat.blending = THREE.AdditiveBlending;
    this.poolMat.side = THREE.DoubleSide;
    this.poolMat.opacity = V.poolOpacity;
    this.poolMat.fog = true;
    this.poolMat.toneMapped = true;
    this.poolMat.needsUpdate = true;

    // WET-ROAD REFLECTION STREAK (§9.3). Same material class as the pool — a private
    // `traffic.light` clone, additive, depth-tested but not depth-written — so three keys it
    // to the SAME shader program and it costs one draw call and zero programs. The colour
    // (saturated red for a tail, warm white for a head) comes through instanceColor.
    this.streakTex = makeStreakTexture(V.streak.tex, V.streak.breakup);
    this.streakMat = mats.instanceOf('traffic.light');
    this.streakMat.name = 'traffic.streak';
    this.streakMat.map = this.streakTex;
    this.streakMat.transparent = true;
    this.streakMat.depthWrite = false;
    this.streakMat.depthTest = true;
    this.streakMat.blending = THREE.AdditiveBlending;
    this.streakMat.side = THREE.DoubleSide;
    this.streakMat.opacity = 1;
    this.streakMat.fog = true;
    this.streakMat.toneMapped = true;
    this.streakMat.needsUpdate = true;
  }

  /** Copy `look`'s weather-driven properties onto every private clone. Four floats each. */
  syncMaterials() {
    for (let i = 0; i < this._graded.length; i++) this._graded[i].sync();
  }

  // ---------------------------------------------------------------- meshes

  /**
   * `shadow` is `true` (cast + receive), `false` (neither), or `'receive'`.
   *
   * `'receive'` exists because CASTING is charged twice — the shadow map re-renders every
   * caster, and `tools/tribudget.mjs` measures that pass at 20.3% of the gated triangle count
   * on `winter-slush` — while RECEIVING is free. A pedestrian is 0.5 m wide: its cast shadow is
   * a smear nobody can resolve, but a pedestrian that does not RECEIVE shadow is a brightly lit
   * figure standing inside a building's shade, which reads as a sticker. So peds keep the half
   * that matters and drop the half that costs.
   */
  _inst(geo, mat, capacity, name, shadow) {
    const m = new THREE.InstancedMesh(geo, mat, capacity);
    m.name = name;
    m.count = 0;
    m.visible = false;
    m.frustumCulled = false;                    // we distance-cull per instance instead
    m.castShadow = shadow === true;
    m.receiveShadow = !!shadow;
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // Allocate the colour buffer up front so the first setColorAt never reallocates.
    m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3).fill(1), 3);
    m.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.group.add(m);
    return m;
  }

  _buildMeshes() {
    const P = T.population;
    const models = this.models;

    this.bodyMesh = {};
    this.kindIndex = {};
    for (let i = 0; i < models.kindNames.length; i++) {
      const k = models.kindNames[i];
      this.kindIndex[k] = i;
      this.bodyMesh[k] = this._inst(models.kinds[k].geo, this.paint.mat, P.maxCars, 'traffic.body.' + k, true);
    }
    this.kindList = models.kindNames;

    this.wheelMesh = this._inst(models.wheel, this.tyre.mat, P.maxCars * 6 + P.maxCyclists * 2, 'traffic.wheels', false);
    this.cyclistMesh = this._inst(models.cyclist.rider, this.cloth.mat, P.maxCyclists, 'traffic.cyclists.rider', true);
    this.bikeMesh = this._inst(models.cyclist.bike, this.tyre.mat, P.maxCyclists, 'traffic.cyclists.bike', true);
    /**
     * PEDESTRIANS — three tint channels, and one body mesh PER ARCHETYPE.
     *
     * `pedBodyMesh` is an array indexed by archetype (models.js § pedestrians explains why a
     * tint cannot stand in for a silhouette). All of them share the one `cloth.mat` clone, so
     * the archetypes cost draw calls but not shader programs.
     *
     * `pedLegsMesh` is the round-11 fix for "one value and a capsule": the legs used to ride
     * the coat mesh, so trousers were `coat × 0.26` and every pedestrian was one hue from
     * collar to shoe. On their own mesh they carry their own instanceColor.
     *
     * Capacity: each body mesh must be able to hold ALL peds, because the archetype mix is a
     * property of the spawned population and nothing caps any one of them.
     */
    this.pedBodyMesh = models.pedBodies.map((geo, i) =>
      this._inst(geo, this.cloth.mat, P.maxPeds, 'traffic.peds.body.' + T.pedArchetypes[i].id, 'receive'));
    /**
     * HANDS, one mesh per archetype (round 12). They carry the SKIN tint, so they cannot ride
     * the coat-tinted body mesh; and they follow the archetype's arm pose, so they cannot ride
     * the one shared head mesh either. One draw call per archetype, no new shader program —
     * they share the `skin.mat` clone with the head.
     */
    this.pedHandMesh = models.pedHands.map((geo, i) =>
      this._inst(geo, this.skin.mat, P.maxPeds, 'traffic.peds.hands.' + T.pedArchetypes[i].id, 'receive'));
    /**
     * LEGS, one mesh per STRIDE POSE (round 12), chosen per instance and independently of the
     * archetype. Costs a draw call each and zero triangles per instance — a pedestrian still
     * draws exactly one legs mesh. This is the half of the pose fix that four critics asked
     * for: "the same mesh in the same pose within 20 px of each other".
     */
    this.pedLegsMesh = models.pedLegs.map((geo, i) =>
      this._inst(geo, this.cloth.mat, P.maxPeds, 'traffic.peds.legs.' + T.pedLegPoses[i].id, 'receive'));
    this.pedHeadMesh = this._inst(models.pedHead, this.skin.mat, P.maxPeds, 'traffic.peds.head', 'receive');
    this._nPedBody = new Int32Array(models.pedBodies.length);
    this._nPedLegs = new Int32Array(models.pedLegs.length);

    this.headMesh = this._inst(models.lamp, this.headMat, P.maxCars * 2, 'traffic.lamps.head', false);
    this.tailMesh = this._inst(models.lamp, this.brakeMat, P.maxCars * 2 + P.maxCyclists, 'traffic.lamps.tail', false);
    this.indMesh = this._inst(models.lamp, this.indMat, P.maxCars * 4, 'traffic.lamps.indicator', false);
    this.poolMesh = this._inst(models.pool, this.poolMat, P.maxCars * 2, 'traffic.lightpool', false);
    this.poolMesh.renderOrder = 3;

    // Exactly ONE streak per agent — you only see the reflection of the lamp pair pointing at
    // you. Drawn UNDER the pool so a car's own headlight cone sits on top of its reflection
    // rather than being cut by it.
    this.streakMesh = this._inst(models.streak, this.streakMat, P.maxCars + P.maxCyclists,
      'traffic.streak', false);
    this.streakMesh.renderOrder = 2;

    /**
     * Hard contact shadow, one per vehicle, cyclist AND PEDESTRIAN. Rides the tyre material so
     * it costs a draw call and no shader program. Never receives the real shadow map: a shadow
     * inside a shadow disappears, and the whole point is that the object reads as planted.
     *
     * ROUND 12 — PEDESTRIANS WERE NOT IN THIS MESH, AND THAT IS THE INCONSISTENCY THE CRITICS
     * MEASURED. `fx` auto-draws one blob per entity and offsets it along the sun vector
     * (§4.8b), deliberately, so it covers the ground the body hides. Cars and cyclists then get
     * a SECOND, centred blob from here — that pairing is the round-8 measurement recorded in
     * TUNING.visual.shadowValue, and it is why a car lands on §3.4's (34,40,35) and a
     * pedestrian did not. A ped had only the offset one, which is exactly the report: *"one
     * figure has no contact shadow at all; another's shadow ellipse is offset ~8 px from where
     * its legs terminate."* Adding peds here costs zero draw calls (same mesh) and 6 triangles
     * each.
     */
    this.blobMesh = this._inst(models.blob, this.tyre.mat,
      P.maxCars + P.maxCyclists + P.maxPeds, 'traffic.blobshadow', false);

    this._all = [
      ...Object.values(this.bodyMesh),
      this.wheelMesh, this.cyclistMesh, this.bikeMesh,
      ...this.pedBodyMesh, ...this.pedHandMesh, ...this.pedLegsMesh, this.pedHeadMesh,
      this.headMesh, this.tailMesh, this.indMesh, this.poolMesh, this.streakMesh, this.blobMesh,
    ];
  }

  // ---------------------------------------------------------------- per-step writes

  begin() {
    const n = this._n;
    n.wheel = 0; n.head = 0; n.tail = 0; n.ind = 0; n.pool = 0; n.cyclist = 0; n.ped = 0;
    n.blob = 0; n.streak = 0;
    this._nBody.fill(0);
    this._nPedBody.fill(0);
    this._nPedLegs.fill(0);
  }

  /** Reusable slot writer. Returns the instance index used, or −1 when the mesh is full. */
  _push(mesh, count, x, y, z, ry, rx, rz, sx, sy, sz, col) {
    if (count >= mesh.instanceMatrix.count) return -1;
    const d = this._dummy;
    d.position.set(x, y, z);
    d.rotation.set(rx, ry, rz);
    d.scale.set(sx, sy, sz);
    d.updateMatrix();
    mesh.setMatrixAt(count, d.matrix);
    if (col) {
      mesh.instanceColor.array[count * 3] = col[0];
      mesh.instanceColor.array[count * 3 + 1] = col[1];
      mesh.instanceColor.array[count * 3 + 2] = col[2];
    }
    return count;
  }

  vehicle(kindIdx, x, y, z, yaw, pitch, roll, col) {
    const kind = this.kindList[kindIdx];
    const mesh = this.bodyMesh[kind];
    const i = this._nBody[kindIdx];
    if (this._push(mesh, i, x, y, z, yaw, pitch, roll, 1, 1, 1, col) >= 0) this._nBody[kindIdx] = i + 1;
  }

  wheel(x, y, z, yaw, spin, r, w) {
    const n = this._n.wheel;
    if (this._push(this.wheelMesh, n, x, y, z, yaw, spin, 0, w, r, r, null) >= 0) this._n.wheel = n + 1;
  }

  lampHead(x, y, z, yaw, size) {
    const n = this._n.head;
    if (this._push(this.headMesh, n, x, y, z, yaw, 0, 0, size, size, 1, null) >= 0) this._n.head = n + 1;
  }

  lampTail(x, y, z, yaw, size) {
    const n = this._n.tail;
    if (this._push(this.tailMesh, n, x, y, z, yaw, 0, 0, size, size, 1, null) >= 0) this._n.tail = n + 1;
  }

  lampInd(x, y, z, yaw, size) {
    const n = this._n.ind;
    if (this._push(this.indMesh, n, x, y, z, yaw, 0, 0, size, size, 1, null) >= 0) this._n.ind = n + 1;
  }

  lightPool(x, y, z, yaw, width, length) {
    const n = this._n.pool;
    if (this._push(this.poolMesh, n, x, y, z, yaw, 0, 0, width, 1, length, null) >= 0) this._n.pool = n + 1;
  }

  /**
   * WET-ROAD REFLECTION STREAK. `y` is the GROUND sample (§4.9c), `yaw` is the direction the
   * streak RUNS IN, and `gain` scales the source-end brightness — the streak is additive, so
   * gain is what puts it 3–4× above the road beside it (`night-rain` criterion 5).
   */
  streak(x, y, z, yaw, width, length, rgb, gain) {
    const n = this._n.streak;
    const c = this._streakCol;
    c[0] = rgb[0] * gain; c[1] = rgb[1] * gain; c[2] = rgb[2] * gain;
    if (this._push(this.streakMesh, n, x, y + V.streak.y, z, yaw, 0, 0, width, 1, length, c) >= 0) {
      this._n.streak = n + 1;
    }
  }

  /** Ground-pinned contact shadow. `y` is the GROUND sample, not the body height. */
  blob(x, y, z, yaw, w, l) {
    const n = this._n.blob;
    if (this._push(this.blobMesh, n, x, y + V.shadowY, z, yaw, 0, 0, w, 1, l, null) >= 0) this._n.blob = n + 1;
  }

  cyclist(x, y, z, yaw, roll, col) {
    const n = this._n.cyclist;
    if (this._push(this.cyclistMesh, n, x, y, z, yaw, 0, roll, 1, 1, 1, col) < 0) return;
    this._push(this.bikeMesh, n, x, y, z, yaw, 0, roll, 1, 1, 1, null);
    this._n.cyclist = n + 1;
  }

  /**
   * One pedestrian. `arch` selects the body geometry; `sxz`/`sy` are the per-instance build
   * scale, applied IDENTICALLY to all three meshes so the head stays on the shoulders and the
   * legs stay under the hips — every geometry is authored in the same frame with y = 0 at the
   * sole, so a common scale keeps them registered.
   *
   * The legs and the head use their own instance slot counters, NOT the body's, because the
   * body slot index is per-archetype.
   */
  ped(x, y, z, yaw, lean, arch, legPose, coat, trouser, skin, sxz, sy) {
    const n = this._n.ped;
    const a = arch < this.pedBodyMesh.length ? arch : 0;
    const lp = legPose < this.pedLegsMesh.length ? legPose : 0;
    const bi = this._nPedBody[a];
    const li = this._nPedLegs[lp];
    if (this._push(this.pedBodyMesh[a], bi, x, y, z, yaw, lean, 0, sxz, sy, sxz, coat) < 0) return;
    if (this._push(this.pedLegsMesh[lp], li, x, y, z, yaw, lean, 0, sxz, sy, sxz, trouser) < 0) return;
    this._push(this.pedHandMesh[a], bi, x, y, z, yaw, lean, 0, sxz, sy, sxz, skin);
    this._push(this.pedHeadMesh, n, x, y, z, yaw, lean, 0, sxz, sy, sxz, skin);
    this._nPedBody[a] = bi + 1;
    this._nPedLegs[lp] = li + 1;
    this._n.ped = n + 1;
  }

  /** Publish the counts and flag the buffers. One call per fixed step. */
  commit() {
    for (let i = 0; i < this.kindList.length; i++) {
      const m = this.bodyMesh[this.kindList[i]];
      this._setCount(m, this._nBody[i]);
    }
    this._setCount(this.wheelMesh, this._n.wheel);
    this._setCount(this.cyclistMesh, this._n.cyclist);
    this._setCount(this.bikeMesh, this._n.cyclist);
    for (let i = 0; i < this.pedBodyMesh.length; i++) {
      // The hands mesh is indexed by archetype and filled in lockstep with the body, so it
      // shares the body's counter — one slot per figure, never a mismatch.
      this._setCount(this.pedBodyMesh[i], this._nPedBody[i]);
      this._setCount(this.pedHandMesh[i], this._nPedBody[i]);
    }
    for (let i = 0; i < this.pedLegsMesh.length; i++) this._setCount(this.pedLegsMesh[i], this._nPedLegs[i]);
    this._setCount(this.pedHeadMesh, this._n.ped);
    this._setCount(this.headMesh, this._n.head);
    this._setCount(this.tailMesh, this._n.tail);
    this._setCount(this.indMesh, this._n.ind);
    this._setCount(this.poolMesh, this._n.pool);
    this._setCount(this.streakMesh, this._n.streak);
    this._setCount(this.blobMesh, this._n.blob);

    let bodies = 0;
    for (let i = 0; i < this._nBody.length; i++) bodies += this._nBody[i];
    this.drawn.bodies = bodies;
    this.drawn.wheels = this._n.wheel;
    this.drawn.lamps = this._n.head + this._n.tail + this._n.ind;
    this.drawn.pools = this._n.pool;
    this.drawn.cyclists = this._n.cyclist;
    this.drawn.peds = this._n.ped;
    this.drawn.blobs = this._n.blob;
    this.drawn.streaks = this._n.streak;
  }

  _setCount(mesh, n) {
    mesh.count = n;
    mesh.visible = n > 0;
    if (n > 0) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }

  setPoolOpacity(v) {
    this.poolMat.opacity = v;
    this.poolMat.visible = v > 0.001;
  }

  /** Global fade for the wet-road trails. 0 in dry weather, so a daylight shot pays nothing. */
  setStreakOpacity(v) {
    this.streakMat.opacity = v;
    this.streakMat.visible = v > 0.001;
  }

  dispose() {
    for (const m of this._all) {
      this.group.remove(m);
      m.dispose();
    }
    for (const gm of this._graded) gm.dispose();
    this.poolMat.dispose();
    this.poolTex.dispose();
    this.streakMat.dispose();
    this.streakTex.dispose();
    if (this.group.parent) this.group.parent.remove(this.group);
  }
}

/** Absolute linear colour triples for the paint palette, prebuilt once. */
export function paintColours() {
  const out = [];
  for (let i = 0; i < TRAFFIC.paints.length; i++) out.push(hexToLinear(TRAFFIC.paints[i]));
  return out;
}

export function taxiColour() { return hexToLinear(TRAFFIC.taxiWhite); }
export function busColour() { return hexToLinear(TRAFFIC.busBlue); }

export function jacketColours() {
  const out = [];
  for (const hex of T.bike.jacketHexes) out.push(hexToLinear(hex));
  return out;
}

export function coatColours() {
  const out = [];
  for (const hex of T.pedModel.coatHexes) out.push(hexToLinear(hex));
  return out;
}

/** The independent trouser channel — see `TUNING.pedModel.trouserHexes`. */
export function trouserColours() {
  const out = [];
  for (const hex of T.pedModel.trouserHexes) out.push(hexToLinear(hex));
  return out;
}

export function skinColours() {
  const out = [];
  for (const hex of CHAR.skin) out.push(hexToLinear(hex));
  return out;
}
